import { BaseModel, BaseItemData, ItemType, DisplayableItem } from './base.model';

export interface EncryptedSearchArgs {
  iv: string;
  encrypted: string;
}

// Pode ser criptografado ou não criptografado (para mocks/testes)
export type SearchArgsType = EncryptedSearchArgs | Record<string, string>;

export interface InvalidEntryReport {
  report_type: string;
  report_search_args: SearchArgsType;
  user_wrapped_dek?: string | null;
  user_dek_iv?: string | null;
}

// Dados necessários para o retry de um batch
export interface BatchRetryData {
  batchId: string;
  parentFolderId: string | null;
  invalidEntries: InvalidEntryReport[];
}

export interface BatchReportData {
  batch_id: string;
  status_code: string;
  total: number;
  done: number;
  failed: number;
  remaining: number;
  report_ids?: string[];
  parent_folder_id?: string | null;
  invalid_entries_report_list?: InvalidEntryReport[];
}

export interface NormalizedBatch extends BaseItemData {
  statusCode: string;
  total: number;
  done: number;
  failed: number;
  remaining: number;
  reportIds?: string[];
  parentFolderId?: string | null;
  invalidEntriesReportList?: InvalidEntryReport[];
  type: ItemType.BATCH;
}

export class BatchModel implements BaseModel<NormalizedBatch>, DisplayableItem {
  readonly type = ItemType.BATCH;

  constructor(public readonly raw: BatchReportData) { }

  get id(): string {
    return this.raw.batch_id;
  }

  get name(): string {
    return `Múltiplos Relatórios`;
  }

  get statusCode(): string {
    return this.raw.status_code;
  }

  get total(): number {
    return this.raw.total;
  }

  get done(): number {
    return this.raw.done;
  }

  get failed(): number {
    return this.raw.failed;
  }

  get remaining(): number {
    return this.raw.remaining;
  }

  get reportIds(): string[] | undefined {
    return this.raw.report_ids;
  }

  get parentFolderId(): string | null | undefined {
    return this.raw.parent_folder_id;
  }

  get invalidEntriesReportList(): InvalidEntryReport[] {
    return this.raw.invalid_entries_report_list ?? [];
  }

  get hasInvalidEntries(): boolean {
    return this.invalidEntriesReportList.length > 0;
  }

  get invalidEntriesCount(): number {
    return this.invalidEntriesReportList.length;
  }

  get isProcessing(): boolean {
    return this.statusCode === 'processing';
  }

  get isCompleted(): boolean {
    return this.statusCode === 'completed';
  }

  get isCancelled(): boolean {
    return this.statusCode === 'cancelled';
  }

  get progressPercentage(): number {
    return this.total > 0 ? Math.round((this.done / this.total) * 100) : 0;
  }

  get retryData(): BatchRetryData {
    return {
      batchId: this.id,
      parentFolderId: this.parentFolderId ?? null,
      invalidEntries: this.invalidEntriesReportList,
    };
  }

  toJSON(): NormalizedBatch {
    return {
      id: this.id,
      name: this.name,
      statusCode: this.statusCode,
      total: this.total,
      done: this.done,
      failed: this.failed,
      remaining: this.remaining,
      reportIds: this.reportIds,
      parentFolderId: this.parentFolderId,
      invalidEntriesReportList: this.invalidEntriesReportList,
      type: this.type,
    };
  }
}
